/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

import { loading } from "commonAssets/js/constants";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import { modal } from "commonAssets/lib/modal.js";

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    // console.log("[Initiate claim]...");

    document.getElementById("claimFormDeadline")?.remove();
    document.getElementById("date")?.closest(".col-md-5")?.remove();

    const host =
        typeof window !== "undefined" && window.location
            ? window.location.hostname
            : "";
    const defaultMode = /angeiontest|localhost|127\.0\.0\.1|\.local/i.test(host)
        ? "development"
        : "production";
    const resolvedMode =
        (typeof window !== "undefined" &&
            typeof window.__APP_MODE__ === "string" &&
            window.__APP_MODE__) ||
        (typeof process !== "undefined" &&
            process &&
            process.env &&
            typeof process.env.mode === "string" &&
            process.env.mode) ||
        defaultMode;

    const boldSignEnvironment = resolveBoldSignEnvironment(resolvedMode);

    const boldSignTemplateIds = {
        owner: getTemplateIdFromSources("owner"),
        coOwner: getTemplateIdFromSources("coOwner"),
    };

    const BOLDSIGN_ORIGIN = "https://app.boldsign.com";
    let previousBodyOverflow = "";
    let boldSignMessageHandler = null;

    const boldSignOnBehalfEmail = "confirmation@sierravistasettlement.com";
    const ownerDocumentInput = document.getElementById("owner_document_id");
    const coOwnerDocumentInput = document.getElementById(
        "co_owner_document_id"
    );
    if (
        ownerDocumentInput &&
        ownerDocumentInput.dataset &&
        ownerDocumentInput.dataset.templateId
    ) {
        const val = ownerDocumentInput.dataset.templateId.trim();
        if (val) {
            boldSignTemplateIds.owner = val;
        }
    }
    if (
        coOwnerDocumentInput &&
        coOwnerDocumentInput.dataset &&
        coOwnerDocumentInput.dataset.templateId
    ) {
        const val = coOwnerDocumentInput.dataset.templateId.trim();
        if (val) {
            boldSignTemplateIds.coOwner = val;
        }
    }
    const boldSignButtons = {};
    let coOwnerButtonWrapper = null;
    const isElement = (node) =>
        typeof Element !== "undefined"
            ? node instanceof Element
            : node &&
            typeof node === "object" &&
            node !== null &&
            node.nodeType === 1;
    const showLoader = () => {
        if (isElement(loading)) {
            loadingGif(loading, "show");
        }
    };
    const hideLoader = (timeout = 250) => {
        if (isElement(loading)) {
            loadingGif(loading, "hide", timeout);
        }
    };

    const claimFieldsPanel = document.getElementById("claim-fields");
    if (claimFieldsPanel) {
        const buttonContainer = document.createElement("div");
        buttonContainer.className =
            "d-flex flex-wrap gap-2 align-items-center mt-3";
        buttonContainer.id = "boldsign-button-container";

        const ownerButton = createBoldSignButton("owner", "W9 - Owner");
        boldSignButtons.owner = ownerButton;
        buttonContainer.appendChild(ownerButton);

        coOwnerButtonWrapper = document.createElement("div");
        coOwnerButtonWrapper.className = "d-none";
        const coOwnerButton = createBoldSignButton("coOwner", "W9 - Co-owner");
        boldSignButtons.coOwner = coOwnerButton;
        coOwnerButtonWrapper.appendChild(coOwnerButton);
        buttonContainer.appendChild(coOwnerButtonWrapper);

        claimFieldsPanel.appendChild(buttonContainer);

        if (ownerDocumentInput && ownerDocumentInput.value.trim() !== "") {
            markButtonAsCompleted("owner", ownerDocumentInput.value.trim());
        }
        if (coOwnerDocumentInput && coOwnerDocumentInput.value.trim() !== "") {
            toggleCoOwnerButton(true);
            markButtonAsCompleted("coOwner", coOwnerDocumentInput.value.trim());
        }
    }

    // Disable first and last name if a value already exists
    try {
        ["first_name", "last_name"].forEach((id) => {
            const el = document.getElementById(id);
            if (el) {
                el.disabled = el.value ? true : false;
            }
        });
    } catch {
        // console.warn("[customInitClaim] name disable check skipped:", e);
    }

    // Hide co-owner name fields until "add co-owner" is clicked and manage co-owner signature requirement
    try {
        const container = document.querySelector("#contact-information .row");
        const coFirst = document.getElementById("co_first_name");
        const coLast = document.getElementById("co_last_name");
        if (container && coFirst && coLast) {
            const wrapFirst =
                coFirst.closest(".divContactField") || coFirst.parentElement;
            const wrapLast =
                coLast.closest(".divContactField") || coLast.parentElement;

            wrapFirst.classList.add("co-owner-field");
            wrapLast.classList.add("co-owner-field");

            if (!coFirst.getAttribute("data-label")) {
                coFirst.setAttribute("data-label", "First Name (co-owner)");
            }
            if (!coLast.getAttribute("data-label")) {
                coLast.setAttribute("data-label", "Last Name (co-owner)");
            }

            const certification = document.getElementById("certification");
            const signatureAnchor = certification
                ? certification.querySelector(".row:last-of-type")
                : null;

            const getSignatureRow = () => {
                if (!certification) {
                    return null;
                }
                return certification.querySelector(".co-owner-signature-row");
            };

            const registerSignatureInputs = (row, active) => {
                if (!row) {
                    return;
                }
                const claimInputsList =
                    claim && claim.inputs && Array.isArray(claim.inputs.fields)
                        ? claim.inputs.fields
                        : null;
                if (!claimInputsList) {
                    return;
                }
                const inputs = Array.prototype.slice.call(
                    row.querySelectorAll("#co_owner_signature, #co_owner_date")
                );
                if (!inputs.length) return;
                if (active) {
                    inputs.forEach((input) => {
                        if (claimInputsList.indexOf(input) === -1) {
                            claimInputsList.push(input);
                        }
                    });
                } else {
                    claim.inputs.fields = claimInputsList.filter(
                        (input) => inputs.indexOf(input) === -1
                    );
                }
            };

            const createSignatureRow = () => {
                if (!certification || !signatureAnchor) {
                    return null;
                }

                const row = document.createElement("div");
                row.className = "row g-3 co-owner-signature-row d-none";

                const colSig = document.createElement("div");
                colSig.className = "col-md-5";
                const sigInput = document.createElement("input");
                sigInput.type = "text";
                sigInput.name = "co_owner_signature";
                sigInput.id = "co_owner_signature";
                sigInput.className = "form-control";
                sigInput.setAttribute("placeholder", "TYPE CO-OWNER FULL NAME");
                sigInput.setAttribute("autocomplete", "name");
                sigInput.setAttribute("data-label", "Co-owner Signature");
                sigInput.setAttribute(
                    "data-contact-label",
                    "Co-owner Signature"
                );
                if (typeof window !== "undefined" && window.validateField) {
                    sigInput.addEventListener("change", () =>
                        window.validateField(sigInput)
                    );
                }
                const sigLabel = document.createElement("label");
                sigLabel.setAttribute("for", "co_owner_signature");
                sigLabel.innerHTML =
                    'Co-owner Signature* <span class="error"></span>';
                colSig.append(sigInput, sigLabel);

                const colDate = document.createElement("div");
                colDate.className = "col-md-5";
                const dateInput = document.createElement("input");
                dateInput.type = "text";
                dateInput.name = "co_owner_date";
                dateInput.id = "co_owner_date";
                dateInput.className = "form-control";
                dateInput.setAttribute("data-label", "Co-owner Signature Date");
                dateInput.setAttribute(
                    "data-contact-label",
                    "Co-owner Signature Date"
                );
                dateInput.setAttribute("data-validate", "required");
                dateInput.setAttribute("disabled", "disabled");
                const dateLabel = document.createElement("label");
                dateLabel.setAttribute("for", "co_owner_date");
                dateLabel.innerHTML =
                    'Date (Co-owner) <span class="error"></span>';
                colDate.append(dateInput, dateLabel);

                const colLegend = document.createElement("div");
                colLegend.className =
                    "col-md-2 d-flex align-items-center justify-content-md-end justify-content-start";
                const legendSpan = document.createElement("span");
                legendSpan.className = "co-owner-sign-label d-none";
                legendSpan.textContent = "Sign";
                colLegend.appendChild(legendSpan);

                row.append(colSig, colDate, colLegend);
                certification.insertBefore(row, signatureAnchor);
                return row;
            };

            const syncSignatureDate = (row) => {
                if (!row) return;
                const dateInput = row.querySelector("#co_owner_date");
                if (!dateInput) return;
                const primaryDate = document.getElementById("date");
                if (primaryDate && primaryDate.value) {
                    dateInput.value = primaryDate.value;
                } else {
                    dateInput.value = new Date().toLocaleString();
                }
            };

            const resetSignatureRow = () => {
                const row = getSignatureRow();
                if (!row) return;
                const sigInput = row.querySelector("#co_owner_signature");
                const dateInput = row.querySelector("#co_owner_date");
                if (sigInput) {
                    sigInput.value = "";
                    sigInput.removeAttribute("required");
                    sigInput.removeAttribute("aria-required");
                    sigInput.removeAttribute("data-validate");
                    sigInput.classList.remove("error", "warning");
                    const errorSpan = row.querySelector(
                        'label[for="co_owner_signature"] .error'
                    );
                    if (errorSpan) errorSpan.textContent = "";
                }
                if (dateInput) {
                    dateInput.value = "";
                }
                registerSignatureInputs(row, false);
                row.remove();
            };

            const toggleSignatureRow = () => {
                const coOwnerVisible =
                    !wrapFirst.classList.contains("d-none") ||
                    !wrapLast.classList.contains("d-none");
                const hasNameValue =
                    Boolean((coFirst.value || "").trim()) ||
                    Boolean((coLast.value || "").trim());
                if (!coOwnerVisible && !hasNameValue) {
                    resetSignatureRow();
                    return;
                }

                let row = getSignatureRow();
                if (!row) {
                    row = createSignatureRow();
                }
                if (!row) return;

                row.classList.remove("d-none");
                const sigInput = row.querySelector("#co_owner_signature");
                if (sigInput) {
                    sigInput.setAttribute("required", "");
                    sigInput.setAttribute("aria-required", "true");
                    sigInput.setAttribute("data-validate", "required");
                }
                registerSignatureInputs(row, true);
                syncSignatureDate(row);
            };

            const setCoOwnerVisibleClass = (visible) => {
                const firstClasses = [
                    "co-owner-visible",
                    "co-owner-first-visible",
                ];
                const lastClasses = ["co-owner-visible"];
                if (visible) {
                    wrapFirst.classList.add(...firstClasses);
                    wrapLast.classList.add(...lastClasses);
                } else {
                    wrapFirst.classList.remove(...firstClasses);
                    wrapLast.classList.remove(...lastClasses);
                }
                toggleCoOwnerButton(visible);
            };

            const hasInitialValue =
                Boolean((coFirst.value || "").trim()) ||
                Boolean((coLast.value || "").trim());

            if (!hasInitialValue) {
                wrapFirst.classList.add("d-none");
                wrapLast.classList.add("d-none");
                setCoOwnerVisibleClass(false);

                const btnWrap = document.createElement("div");
                btnWrap.className = "col mb-3";
                btnWrap.id = "co-owner-btn-wrapper";
                const btn = document.createElement("button");
                btn.type = "button";
                btn.className = "btn btn-sm btn-primary";
                btn.id = "add-co-owner";
                btn.textContent = "Add co-owner";
                const controlledIds = [wrapFirst.id, wrapLast.id]
                    .filter(Boolean)
                    .join(" ");
                if (controlledIds) {
                    btn.setAttribute("aria-controls", controlledIds);
                }
                btn.setAttribute("aria-expanded", "false");
                btn.setAttribute(
                    "aria-label",
                    "Add co-owner contact information"
                );
                btn.addEventListener("click", () => {
                    wrapFirst.classList.remove("d-none");
                    wrapLast.classList.remove("d-none");
                    setCoOwnerVisibleClass(true);
                    toggleSignatureRow();
                    btn.setAttribute("aria-expanded", "true");
                    btnWrap.remove();
                    coFirst.focus();
                });
                btnWrap.appendChild(btn);

                const lastNameField = document.getElementById("last_name");
                const lastNameContainer = lastNameField
                    ? lastNameField.closest(".divContactField")
                    : null;
                if (
                    lastNameContainer &&
                    lastNameContainer.parentElement === container
                ) {
                    lastNameContainer.insertAdjacentElement(
                        "afterend",
                        btnWrap
                    );
                } else {
                    container.appendChild(btnWrap);
                }
            } else {
                setCoOwnerVisibleClass(true);
            }

            toggleSignatureRow();

            coFirst.addEventListener("input", () => toggleSignatureRow());
            coLast.addEventListener("input", () => toggleSignatureRow());
        }
    } catch {
        // console.warn("[customInitClaim] co-owner controller skipped:", e);
    }

    // Enforce MM/YYYY mask for transactional field:
    // data-label="Ownership Start (MM/YYYY)" and persist for dynamically added rows
    try {
        const selector = 'input[data-label="Ownership Start (MM/YYYY)"]';

        const formatAsMonthYear = (input) => {
            if (!input) return;
            // keep digits only, limit to 6 (MMYYYY)
            const digits = (input.value || "").replace(/\D/g, "").slice(0, 6);
            if (digits.length <= 2) {
                input.value = digits; // allow partial month while typing
            } else {
                input.value = `${digits.slice(0, 2)}/${digits.slice(2)}`;
            }
        };

        const normalizeMonthYear = (input) => {
            const v = (input.value || "").trim();
            if (!/^\d{2}\/\d{4}$/.test(v)) return; // only clamp complete values
            const [mm, yyyy] = v.split("/");
            const m = Math.min(Math.max(parseInt(mm, 10) || 0, 1), 12);
            // persist normalized mm to two digits
            input.value = `${String(m).padStart(2, "0")}/${yyyy}`;
        };

        // Initialize existing inputs with attributes and formatting
        document.querySelectorAll(selector).forEach((el) => {
            el.setAttribute("maxlength", "7"); // MM/YYYY
            el.setAttribute("inputmode", "numeric");
            formatAsMonthYear(el);
        });

        // Delegate to handle dynamically added rows
        document.addEventListener("input", (e) => {
            const t = e.target;
            if (t && t.matches && t.matches(selector)) {
                // ensure expected attributes present
                if (!t.hasAttribute("maxlength"))
                    t.setAttribute("maxlength", "7");
                if (!t.hasAttribute("inputmode"))
                    t.setAttribute("inputmode", "numeric");
                formatAsMonthYear(t);
            }
        });

        document.addEventListener("paste", (e) => {
            const t = e.target;
            if (t && t.matches && t.matches(selector)) {
                // allow paste, then format on next tick
                setTimeout(() => formatAsMonthYear(t), 0);
            }
        });

        // Normalize on blur (completed input)
        document.addEventListener(
            "blur",
            (e) => {
                const t = e.target;
                if (t && t.matches && t.matches(selector)) {
                    normalizeMonthYear(t);
                }
            },
            true
        );
    } catch {
        // console.warn("[customInitClaim] MM/YYYY controller skipped:", e);
    }

    // Limit Months Owned to a maximum of 63 (persist for dynamic rows)
    try {
        const selector = 'input[name="ownership_months"]';
        const MAX_MONTHS = 63;

        const enforceMonthsBounds = (input) => {
            if (!input) return;
            // digits only; cap to two digits
            const digits = (input.value || "").replace(/\D/g, "").slice(0, 2);
            if (digits === "") {
                input.value = "";
                return;
            }
            let n = parseInt(digits, 10);
            if (Number.isNaN(n)) {
                input.value = "";
                return;
            }
            if (n < 0) n = 0;
            if (n > MAX_MONTHS) n = MAX_MONTHS;
            input.value = String(n);
        };

        // Initialize any existing inputs
        document.querySelectorAll(selector).forEach((el) => {
            el.setAttribute("inputmode", "numeric");
            el.setAttribute("maxlength", "2");
            el.setAttribute("min", "0");
            el.setAttribute("max", String(MAX_MONTHS));
            el.setAttribute("step", "1");
            enforceMonthsBounds(el);
        });

        // Delegate handlers for dynamically added inputs
        document.addEventListener(
            "input",
            (e) => {
                const t = e.target;
                if (t && t.matches && t.matches(selector)) {
                    if (!t.hasAttribute("inputmode"))
                        t.setAttribute("inputmode", "numeric");
                    if (!t.hasAttribute("maxlength"))
                        t.setAttribute("maxlength", "2");
                    if (!t.hasAttribute("min")) t.setAttribute("min", "0");
                    if (!t.hasAttribute("max"))
                        t.setAttribute("max", String(MAX_MONTHS));
                    if (!t.hasAttribute("step")) t.setAttribute("step", "1");
                    enforceMonthsBounds(t);
                }
            },
            true
        );

        document.addEventListener("paste", (e) => {
            const t = e.target;
            if (t && t.matches && t.matches(selector)) {
                setTimeout(() => enforceMonthsBounds(t), 0);
            }
        });

        document.addEventListener(
            "blur",
            (e) => {
                const t = e.target;
                if (t && t.matches && t.matches(selector)) {
                    enforceMonthsBounds(t);
                }
            },
            true
        );
    } catch {
        // console.warn("[customInitClaim] Months Owned controller skipped:", e);
    }

    // Make all transactional fields required for all rows; persist on add/remove
    try {
        const containerSelector = "#transactions";

        const setRequired = (el) => {
            if (!el) return;
            el.setAttribute("required", "");
            el.setAttribute("aria-required", "true");
            const dv = (el.getAttribute("data-validate") || "").trim();
            if (dv === "") {
                el.setAttribute("data-validate", "required");
            } else if (!dv.split(/\s+/).includes("required")) {
                el.setAttribute("data-validate", `${dv} required`);
            }
        };

        const applyAllRowsRequired = () => {
            const container = document.querySelector(containerSelector);
            if (!container) return;
            container
                .querySelectorAll(
                    "tr.transaction-row input, tr.transaction-row select, tr.transaction-row textarea"
                )
                .forEach(setRequired);
        };

        // Apply immediately if present
        applyAllRowsRequired();

        // Observe for dynamic changes within the transactions container
        const observeContainer = () => {
            const container = document.querySelector(containerSelector);
            if (!container) return null;
            const obs = new MutationObserver(() => applyAllRowsRequired());
            obs.observe(container, { childList: true, subtree: true });
            return obs;
        };

        let containerObserver = observeContainer();
        if (!containerObserver) {
            // Wait for the transactions container to be inserted
            const docObserver = new MutationObserver(() => {
                if (!containerObserver) {
                    containerObserver = observeContainer();
                    if (containerObserver) docObserver.disconnect();
                }
            });
            docObserver.observe(document.body, {
                childList: true,
                subtree: true,
            });
        }
    } catch {
        // console.warn(
        //     "[customInitClaim] Transactional required setup skipped:",
        //     e
        // );
    }

    // Make each column header 25% width for the Home(s) transactional table
    try {
        const expected = [
            "Class Subdivision",
            "Address of Your Home",
            "Date You Became Owner of Your Home (MM/YYYY)",
            "Number of Months You've Owned Your Home During Class Period",
        ];
        document
            .querySelectorAll("#transactions thead.table-dark.text-light")
            .forEach((thead) => {
                const ths = Array.from(
                    thead.querySelectorAll('th[scope="col"]')
                );
                const titles = ths.map((th) => th.textContent.trim());
                if (
                    titles.length === expected.length &&
                    expected.every((t, i) => t === titles[i])
                ) {
                    ths.forEach((th) => th.classList.add("w-25"));
                }
            });
    } catch {
        // console.warn("[customInitClaim] w-25 header width setup skipped:", e);
    }
    try {
        const claimFieldsData =
            claim && claim.class_member
                ? claim.class_member.claim_fields
                : null;
        const hasExistingClaim =
            !!(claim && claim.data && claim.data.claim_id) &&
            Array.isArray(claimFieldsData) &&
            claimFieldsData.length >= 2 &&
            claimFieldsData[0] &&
            claimFieldsData[1] &&
            typeof claimFieldsData[0].name === "string" &&
            typeof claimFieldsData[1].name === "string" &&
            (claimFieldsData[0].name || "").trim() ===
            "First Name (co-owner)" &&
            typeof claimFieldsData[0].value === "string" &&
            claimFieldsData[0].value.trim() !== "" &&
            (claimFieldsData[1].name || "").trim() === "Last Name (co-owner)" &&
            typeof claimFieldsData[1].value === "string" &&
            claimFieldsData[1].value.trim() !== "";
        let coOwnerSignatureField = null;
        if (Array.isArray(claimFieldsData)) {
            coOwnerSignatureField = claimFieldsData.find(function (field) {
                if (!field || typeof field !== "object") return false;
                const fieldName = (field.name || "").trim();
                const fieldValue =
                    typeof field.value === "string" ? field.value.trim() : "";
                return fieldName === "Co-owner Signature" && fieldValue !== "";
            });
        }
        const ensureSignatureValue = () => {
            const signatureInput =
                document.getElementById("co_owner_signature");
            if (!signatureInput) return false;
            signatureInput.removeAttribute("disabled");
            signatureInput.disabled = false;
            if (coOwnerSignatureField) {
                signatureInput.value = coOwnerSignatureField.value;
            }
            return true;
        };
        const enforceDateDisabled = () => {
            const dateInput = document.getElementById("co_owner_date");
            if (!dateInput) return false;
            dateInput.setAttribute("disabled", "disabled");
            dateInput.disabled = true;
            return true;
        };

        if (hasExistingClaim) {
            const coFirstInput = document.getElementById("co_first_name");
            const coLastInput = document.getElementById("co_last_name");

            if (coFirstInput) {
                coFirstInput.value = claimFieldsData[0].value;
                const wrapFirst =
                    coFirstInput.closest(".divContactField") ||
                    coFirstInput.parentElement;
                if (wrapFirst) {
                    wrapFirst.classList.remove("d-none");
                    wrapFirst.classList.add(
                        "co-owner-visible",
                        "co-owner-first-visible"
                    );
                }
            }

            if (coLastInput) {
                coLastInput.value = claimFieldsData[1].value;
                const wrapLast =
                    coLastInput.closest(".divContactField") ||
                    coLastInput.parentElement;
                if (wrapLast) {
                    wrapLast.classList.remove("d-none");
                    wrapLast.classList.add("co-owner-visible");
                }
            }

            const addBtnWrapper = document.getElementById(
                "co-owner-btn-wrapper"
            );
            if (addBtnWrapper) {
                addBtnWrapper.remove();
            }

            // trigger existing handlers to ensure signature row is created and required
            if (coFirstInput) {
                coFirstInput.dispatchEvent(
                    new Event("input", { bubbles: true })
                );
            }
            if (coLastInput) {
                coLastInput.dispatchEvent(
                    new Event("input", { bubbles: true })
                );
            }

            if (coFirstInput) {
                coFirstInput.setAttribute("disabled", "disabled");
                coFirstInput.disabled = true;
            }
            if (coLastInput) {
                coLastInput.setAttribute("disabled", "disabled");
                coLastInput.disabled = true;
            }

            const signatureRow = document.querySelector(
                "#certification .co-owner-signature-row"
            );
            if (signatureRow) {
                signatureRow.classList.remove("d-none");
                const legend = signatureRow.querySelector(
                    ".co-owner-sign-label"
                );
                if (legend) {
                    legend.classList.remove("d-none");
                }
            }

            if (!ensureSignatureValue()) {
                setTimeout(ensureSignatureValue, 0);
            }
            if (!enforceDateDisabled()) {
                setTimeout(enforceDateDisabled, 0);
            }
        } else if (coOwnerSignatureField) {
            if (!ensureSignatureValue()) {
                setTimeout(ensureSignatureValue, 0);
            }
        }
    } catch {
        // console.warn(
        //     "[customInitClaim] co-owner autofill enablement skipped:",
        //     e
        // );
    }

    function createBoldSignButton(type, label) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "btn btn-primary";
        button.dataset.baseText = label;
        button.textContent = label;
        button.addEventListener("click", () => {
            beginBoldSignFlow(type);
        });
        return button;
    }

    async function beginBoldSignFlow(type) {
        const button = boldSignButtons[type];
        if (
            !boldSignEnvironment.serverlessApiUrl ||
            !boldSignEnvironment.serverlessApiKey
        ) {
            modal(
                "Unable to start",
                "BoldSign configuration is missing. Please contact support."
            );
            return;
        }

        const templateId =
            type === "coOwner"
                ? boldSignTemplateIds.coOwner
                : boldSignTemplateIds.owner;
        if (!templateId) {
            modal(
                "Form Unavailable",
                "The BoldSign template for this signer is not configured. Please contact support."
            );
            return;
        }

        const signerDetails = getSignerDetails(type);
        if (!signerDetails) {
            return;
        }

        if (button) {
            button.classList.add("disabled");
            button.setAttribute("disabled", "disabled");
        }

        try {
            showLoader();
            const claimDomain =
                claim && claim.data && claim.data.domain
                    ? claim.data.domain
                    : "";
            const response = await fetch(
                `${boldSignEnvironment.serverlessApiUrl}/document/bold-sign`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "x-api-key": boldSignEnvironment.serverlessApiKey,
                    },
                    body: JSON.stringify({
                        templateId,
                        signer: {
                            signerName: signerDetails.fullName,
                            signerEmail: signerDetails.email,
                            roleIndex: 1,
                            onBehalfOf: boldSignOnBehalfEmail,
                        },
                        metadata: {
                            domain: claimDomain,
                            signerType: type,
                        },
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(
                    `BoldSign request failed with status ${response.status}`
                );
            }

            const data = await response.json();
            const documentId = data && data.documentId ? data.documentId : "";
            const signerInfo =
                data && data.signer && typeof data.signer === "object"
                    ? data.signer
                    : null;
            const signLink =
                signerInfo && typeof signerInfo.signLink === "string"
                    ? signerInfo.signLink
                    : "";

            if (!documentId || !signLink) {
                throw new Error("BoldSign response missing required fields.");
            }

            if (boldSignMessageHandler) {
                window.removeEventListener("message", boldSignMessageHandler);
                boldSignMessageHandler = null;
            }

            const handleMessage = function (event) {
                if (!event || event.origin !== BOLDSIGN_ORIGIN) {
                    return;
                }

                const action =
                    event && event.data ? event.data.action : undefined;
                if (!action) {
                    return;
                }

                const isSuccess = action === "onDocumentSigned";
                const isTerminalAction = [
                    "onDocumentSigned",
                    "onDocumentSigningFailed",
                    "onDocumentDeclined",
                    "onDocumentReassigned",
                    "onDocumentReassigningFailed",
                    "onDocumentDecliningFailed",
                ].includes(action);

                if (!isTerminalAction) {
                    return;
                }

                if (isSuccess) {
                    markButtonAsCompleted(type, documentId);
                } else {
                    modal(
                        "Signature Incomplete",
                        "The BoldSign session did not complete. Please try again."
                    );
                    if (button && !button.classList.contains("btn-success")) {
                        button.classList.remove("disabled");
                        button.removeAttribute("disabled");
                    }
                }

                window.removeEventListener("message", handleMessage);
                boldSignMessageHandler = null;
                closeBoldSignOverlay();
                hideLoader(150);
            };

            boldSignMessageHandler = handleMessage;
            window.addEventListener("message", handleMessage);
            openBoldSignOverlay(signLink, () => {
                window.removeEventListener("message", handleMessage);
                boldSignMessageHandler = null;
                hideLoader(150);
                if (button && !button.classList.contains("btn-success")) {
                    button.classList.remove("disabled");
                    button.removeAttribute("disabled");
                }
            });
        } catch {
            // console.error("[BoldSign] Failed to initialize session:", error);
            modal(
                "BoldSign Error",
                "We were unable to start the BoldSign session. Please try again later."
            );
            if (button && !button.classList.contains("btn-success")) {
                button.classList.remove("disabled");
                button.removeAttribute("disabled");
            }
        } finally {
            hideLoader(150);
        }
    }

    function getSignerDetails(type) {
        const emailInput = document.getElementById("email_address");
        const confirmEmailInput = document.getElementById(
            "confirm_email_address"
        );
        const email =
            emailInput && typeof emailInput.value === "string"
                ? emailInput.value.trim()
                : "";
        const confirmEmail =
            confirmEmailInput && typeof confirmEmailInput.value === "string"
                ? confirmEmailInput.value.trim()
                : "";

        if (!email || !confirmEmail) {
            modal(
                "Email Required",
                "Please enter and confirm your email address before continuing."
            );
            return null;
        }

        if (email !== confirmEmail) {
            modal(
                "Email Mismatch",
                "The email addresses entered do not match. Please correct them before continuing."
            );
            return null;
        }

        if (type === "owner") {
            const firstNameInput = document.getElementById("first_name");
            const lastNameInput = document.getElementById("last_name");
            const firstName =
                firstNameInput && typeof firstNameInput.value === "string"
                    ? firstNameInput.value.trim()
                    : "";
            const lastName =
                lastNameInput && typeof lastNameInput.value === "string"
                    ? lastNameInput.value.trim()
                    : "";

            if (!firstName || !lastName) {
                modal(
                    "Name Required",
                    "Please provide the owner first and last name before continuing."
                );
                return null;
            }

            return {
                firstName,
                lastName,
                fullName: `${firstName} ${lastName}`.trim(),
                email,
            };
        }

        const coFirstNameInput = document.getElementById("co_first_name");
        const coLastNameInput = document.getElementById("co_last_name");
        const coFirstName =
            coFirstNameInput && typeof coFirstNameInput.value === "string"
                ? coFirstNameInput.value.trim()
                : "";
        const coLastName =
            coLastNameInput && typeof coLastNameInput.value === "string"
                ? coLastNameInput.value.trim()
                : "";

        if (!coFirstName || !coLastName) {
            modal(
                "Co-owner Required",
                "Please enter the co-owner's first and last name before continuing."
            );
            return null;
        }

        return {
            firstName: coFirstName,
            lastName: coLastName,
            fullName: `${coFirstName} ${coLastName}`.trim(),
            email,
        };
    }

    function markButtonAsCompleted(type, documentId) {
        const button = boldSignButtons[type];
        const input =
            type === "coOwner" ? coOwnerDocumentInput : ownerDocumentInput;

        if (input) {
            input.value = documentId;
            input.dispatchEvent(new Event("input", { bubbles: true }));
        }

        if (!button) {
            return;
        }

        button.classList.remove("btn-primary", "disabled");
        button.classList.add("btn-success");
        button.setAttribute("disabled", "disabled");
        button.innerHTML = `<i class="bi bi-check-circle-fill me-1"></i>${button.dataset.baseText}`;
    }

    function toggleCoOwnerButton(visible) {
        if (!coOwnerButtonWrapper) {
            return;
        }
        if (visible) {
            coOwnerButtonWrapper.classList.remove("d-none");
        } else if (
            !(
                coOwnerDocumentInput &&
                coOwnerDocumentInput.value &&
                coOwnerDocumentInput.value.trim() !== ""
            )
        ) {
            coOwnerButtonWrapper.classList.add("d-none");
        }
    }

    function openBoldSignOverlay(signLink, onClose) {
        if (!signLink) {
            return;
        }
        if (typeof document === "undefined" || !document.body) {
            return;
        }
        closeBoldSignOverlay();

        const overlay = document.createElement("div");
        overlay.id = "boldsign-overlay";
        overlay.style.position = "fixed";
        overlay.style.top = "0";
        overlay.style.left = "0";
        overlay.style.width = "100%";
        overlay.style.height = "100%";
        overlay.style.backgroundColor = "rgba(0, 0, 0, 0.75)";
        overlay.style.zIndex = "9998";
        overlay.style.display = "flex";
        overlay.style.alignItems = "center";
        overlay.style.justifyContent = "center";
        overlay.style.padding = "1.5rem";

        const iframe = document.createElement("iframe");
        iframe.src = signLink;
        iframe.style.width = "100%";
        iframe.style.height = "100%";
        iframe.style.maxWidth = "1024px";
        iframe.style.border = "none";
        iframe.style.borderRadius = "12px";
        iframe.style.boxShadow = "0 1rem 3rem rgba(0, 0, 0, 0.3)";

        const closeButton = document.createElement("button");
        closeButton.id = "boldsign-close-button";
        closeButton.textContent = "Close";
        closeButton.type = "button";
        closeButton.style.position = "fixed";
        closeButton.style.top = "1.5rem";
        closeButton.style.right = "1.5rem";
        closeButton.style.zIndex = "9999";
        closeButton.className = "btn btn-light";
        closeButton.addEventListener("click", () => {
            closeBoldSignOverlay();
            if (typeof onClose === "function") {
                onClose();
            }
        });

        overlay.appendChild(iframe);
        document.body.appendChild(overlay);
        document.body.appendChild(closeButton);
        previousBodyOverflow =
            document.body.style &&
                typeof document.body.style.overflow === "string"
                ? document.body.style.overflow
                : "";
        document.body.style.overflow = "hidden";
    }

    function closeBoldSignOverlay() {
        if (typeof document !== "undefined") {
            const overlay = document.getElementById("boldsign-overlay");
            if (overlay && overlay.parentNode) {
                overlay.parentNode.removeChild(overlay);
            }
            const closeButton = document.getElementById(
                "boldsign-close-button"
            );
            if (closeButton && closeButton.parentNode) {
                closeButton.parentNode.removeChild(closeButton);
            }
            if (document.body && document.body.style) {
                document.body.style.overflow = previousBodyOverflow || "";
            }
        }
        previousBodyOverflow = "";
    }
    return claim;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    // console.log("[Load class member]...");
    // custom code goes here
    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(claim) {
    // console.log("[Process claim]...");
    try {
        const coFirst = document.getElementById("co_first_name");
        const coLast = document.getElementById("co_last_name");
        if (coFirst && (coFirst.value || "").trim() !== "") {
            claim.data.contact["co_first_name"] = coFirst.value;
        }
        if (coLast && (coLast.value || "").trim() !== "") {
            claim.data.contact["co_last_name"] = coLast.value;
        }
    } catch {
        // console.warn("[customProcessClaim] co-owner capture skipped:", e);
    }
    return claim;
}

/**
 * Retrieve environment variable values without relying on bundler globals.
 * Supports `process.env`, `window.__ENV__`, and plain global assignments.
 * @param {string} key
 * @returns {string}
 */
function getEnvValue(key) {
    if (!key) {
        return "";
    }
    if (
        typeof window !== "undefined" &&
        window.__ENV__ &&
        typeof window.__ENV__[key] === "string"
    ) {
        return window.__ENV__[key];
    }
    if (
        typeof window !== "undefined" &&
        typeof window[key] === "string" &&
        key.startsWith("BOLDSIGN_")
    ) {
        return window[key];
    }
    if (
        typeof process !== "undefined" &&
        process &&
        process.env &&
        typeof process.env[key] === "string"
    ) {
        return process.env[key];
    }
    return "";
}

/**
 * Determine BoldSign environment configuration by mode, falling back to defaults.
 * @param {string} mode
 * @returns {{serverlessApiUrl: string, serverlessApiKey: string}}
 */
function resolveBoldSignEnvironment(mode) {
    const normalized = (mode || "").toLowerCase();
    const isDevelopment = normalized !== "production";
    const urlEnvKey = isDevelopment
        ? "BOLDSIGN_DEV_API_URL"
        : "BOLDSIGN_PROD_API_URL";
    const keyEnvKey = isDevelopment
        ? "BOLDSIGN_DEV_API_KEY"
        : "BOLDSIGN_PROD_API_KEY";
    const fallbackConfig = isDevelopment
        ? {
            serverlessApiUrl:
                "https://api.serverless.staging.angeiontestsite.com",
            serverlessApiKey: "P7XcibxLNg4lbtPESAsDJaZLVLg7hOyt6R2m8KV9",
        }
        : {
            serverlessApiUrl:
                "https://api.serverless.production.angeiongroup.com",
            serverlessApiKey: "pPOrTIYJuG4p2cpuULf0O87cuzqWB7Bb7i36CxAu",
        };
    return {
        serverlessApiUrl:
            getEnvValue(urlEnvKey) || fallbackConfig.serverlessApiUrl,
        serverlessApiKey:
            getEnvValue(keyEnvKey) || fallbackConfig.serverlessApiKey,
    };
}

/**
 * Resolve BoldSign template ID using environment overrides or defaults.
 * @param {"owner"|"coOwner"} type
 * @returns {string}
 */
function getTemplateIdFromSources(type) {
    const upper = (type || "").toUpperCase();
    const envKey = "BOLDSIGN_TEMPLATE_" + upper;
    const envOverride = getEnvValue(envKey);
    if (envOverride) {
        return envOverride;
    }

    if (
        typeof window !== "undefined" &&
        window.__BOLDSIGN__ &&
        window.__BOLDSIGN__.templates &&
        typeof window.__BOLDSIGN__.templates[type] === "string"
    ) {
        return window.__BOLDSIGN__.templates[type];
    }

    const defaults = {
        owner: "891bca37-e30e-4035-8326-905447d94475",
        coOwner: "891bca37-e30e-4035-8326-905447d94475",
    };
    return defaults[type] || "";
}
